import React from 'react';

import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { CurrencySelector } from '@apigrowfinance/pbolt-currency-selector';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAccount } from '@contexts/AccountProvider';
import useGetCustomerCards from '@hooks/queries/useGetCustomerCards';
import flexControls from '@messages/flexControls/flexControls';
import flexControlsValidations from '@messages/flexControls/flexControlsValidations';
import getEnumTranslation from '@utils/getEnumTranslation';

interface BasicDetailsProps {
    isEditing?: boolean;
    hasTimeZone?: boolean;
}

const BasicDetailsForm = ({
    isEditing,
    hasTimeZone = false,
}: BasicDetailsProps) => {
    const { formatMessage } = useIntl();
    const { theme } = useTheme();
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const { customerId } = useParams();
    const { account } = useAccount();

    const { data: cardData, isLoading: cardsLoading } = useGetCustomerCards(
        account?.account_id,
        parseFloat(customerId),
    );

    const placeholder = formatMessage({
        id: 'BasicDetailsForm.placeholder',
        defaultMessage: 'For multiple values separate each value using a comma',
    });

    const timeZonePlaceholder = formatMessage({
        id: 'BasicDetailsForm.timeZonePlaceholder',
        defaultMessage: 'Value must be a valid IANA time zone name',
    });

    return (
        <>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                {isEditing && (
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            color:
                                theme?.custom?.brand?.textMain ||
                                'text.primary',
                            mb: 2,
                        }}
                        data-testid="basic-details-shared-edit-title">
                        {getEnumTranslation('basic_details', {
                            messages: flexControls,
                        })}
                    </Typography>
                )}
                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            required
                            data-testid="name"
                            {...field}
                            label={
                                <FormattedMessage
                                    id="BasicDetails.name"
                                    defaultMessage="Name"
                                />
                            }
                            fullWidth
                            error={!!errors.name}
                            helperText={
                                !!errors.name && (
                                    <Box component="span">
                                        {getEnumTranslation(
                                            errors?.name?.message.toString(),
                                            {
                                                messages:
                                                    flexControlsValidations,
                                            },
                                        )}
                                    </Box>
                                )
                            }
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="type"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            required
                            data-testid="type-dropdown"
                            select
                            disabled={isEditing}
                            onChange={onChange}
                            value={value}
                            fullWidth
                            error={!!errors.type}
                            helperText={
                                !!errors.type && (
                                    <Box component="span">
                                        {getEnumTranslation(
                                            errors?.type?.message.toString(),
                                            {
                                                messages:
                                                    flexControlsValidations,
                                            },
                                        )}
                                    </Box>
                                )
                            }
                            label={
                                <FormattedMessage
                                    id="BasicDetails.type"
                                    defaultMessage="Type"
                                />
                            }>
                            <MenuItem value="spending_limit">
                                {getEnumTranslation('spending_limit', {
                                    messages: flexControls,
                                })}
                            </MenuItem>
                            <MenuItem value="usage_limit">
                                {getEnumTranslation('usage_limit', {
                                    messages: flexControls,
                                })}
                            </MenuItem>
                            <MenuItem value="restriction">
                                {getEnumTranslation('restriction', {
                                    messages: flexControls,
                                })}
                            </MenuItem>
                        </TextField>
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="processing_codes"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            data-testid="processing-codes"
                            placeholder={placeholder}
                            {...field}
                            label={
                                <FormattedMessage
                                    id="BasicDetails.processingCodes"
                                    defaultMessage="Processing codes"
                                />
                            }
                            fullWidth
                            error={!!errors.processing_codes}
                            helperText={
                                !!errors.processing_codes && (
                                    <>{errors?.processing_codes?.message}</>
                                )
                            }
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="beneficiary_id"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            data-testid="name"
                            {...field}
                            label={
                                <FormattedMessage
                                    id="BasicDetails.beneficiaryId"
                                    defaultMessage="Beneficiary ID"
                                />
                            }
                            fullWidth
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="override_controls"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            data-testid="override-controls"
                            placeholder={placeholder}
                            {...field}
                            label={
                                <FormattedMessage
                                    id="BasicDetails.overrideControls"
                                    defaultMessage="Override controls"
                                />
                            }
                            fullWidth
                            error={!!errors.override_controls}
                            helperText={
                                !!errors.override_controls && (
                                    <>{errors?.override_controls?.message}</>
                                )
                            }
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="currency_code"
                    control={control}
                    render={({ field }) => (
                        <CurrencySelector
                            name="currency_code"
                            alphabeticValues
                            {...field}
                            label={
                                <FormattedMessage
                                    id="BasicDetails.currencyCode"
                                    defaultMessage="Currency code"
                                />
                            }
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="time_zone"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            data-testid="time-zone"
                            placeholder={timeZonePlaceholder}
                            value={value}
                            onChange={({ target: { value } }) =>
                                onChange(value)
                            }
                            required={isEditing && hasTimeZone}
                            label={
                                <FormattedMessage
                                    id="BasicDetails.timezone"
                                    defaultMessage="Time zone"
                                />
                            }
                            fullWidth
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="max_limit"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            type="number"
                            value={value}
                            data-testid="max-limit"
                            onChange={({ target: { value } }) =>
                                onChange(value)
                            }
                            label={
                                <FormattedMessage
                                    id="BasicDetails.maxLimit"
                                    defaultMessage="Max limit"
                                />
                            }
                            fullWidth
                            error={!!errors.max_limit}
                            helperText={
                                !!errors.max_limit && (
                                    <Box component="span">
                                        {getEnumTranslation(
                                            errors?.max_limit?.message.toString(),
                                            {
                                                messages:
                                                    flexControlsValidations,
                                            },
                                        )}
                                    </Box>
                                )
                            }
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={isEditing ? 12 : 6}>
                <Controller
                    name="deny_code"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            data-testid="deny-code"
                            onChange={onChange}
                            value={value}
                            required
                            label={
                                <FormattedMessage
                                    id="BasicDetails.denyCode"
                                    defaultMessage="Deny code"
                                />
                            }
                            fullWidth
                            error={!!errors.deny_code}
                            helperText={
                                !!errors.deny_code && (
                                    <Box component="span">
                                        {getEnumTranslation(
                                            errors?.deny_code?.message.toString(),
                                            {
                                                messages:
                                                    flexControlsValidations,
                                            },
                                        )}
                                    </Box>
                                )
                            }
                        />
                    )}
                />
            </Grid>
            {isEditing && !cardsLoading && (
                <Grid item xs={12} md={isEditing ? 12 : 6}>
                    <Controller
                        name="card_ids"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <FormControl
                                variant="outlined"
                                sx={{ width: '100%' }}>
                                <InputLabel id="cards-select-label">
                                    {getEnumTranslation('cards')}
                                </InputLabel>
                                <Select
                                    labelId="cards-select-label"
                                    sx={{ mb: isEditing ? 2 : 0 }}
                                    data-testid="cards-dropdown"
                                    multiple
                                    fullWidth
                                    defaultValue={[]}
                                    value={value}
                                    label="Cards"
                                    onChange={onChange}
                                    input={<OutlinedInput label="Cards" />}>
                                    {cardData.map((card) => (
                                        <MenuItem
                                            key={card?.id}
                                            value={card?.id}>
                                            {card?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>
            )}
        </>
    );
};

export default BasicDetailsForm;
